// src/components/VoucherPreview.jsx
//
// HTML/CSS voucher-domain preview (frontend).
// ===========================================
//
// USED IN
// -------
//   - Public ShopPage (before payment)
//   - Backoffice preview (VoucherOverviewPage print modal)
//   - Potentially any future create/edit live preview for voucher/package flows
//
// GOAL
// ----
// This component mirrors the same *conceptual* render model used by the
// backend PDF generator (`voucherPdfData`), but in a lightweight,
// browser-friendly way.
//
// It is intentionally safe:
//   - It never uses real voucher tokens / QR payloads.
//   - The voucher number can be a placeholder.
//   - It is purely visual – no sensitive data is derived here.
//
// IMPORTANT ARCHITECTURAL UPDATE
// ------------------------------
// Historically this component was effectively "voucher-only" in its mental model,
// even though it was already generic enough to preview most of the content.
//
// That is no longer sufficient.
//
// The frontend/backoffice architecture now explicitly distinguishes two business
// families:
//
//   1) voucher
//   2) package
//
// even though both still belong to the broader voucher-domain model.
//
// Therefore this component is now BUSINESS-AWARE.
//
// In practice that means:
//
//   - it accepts `preview.businessMode` when available,
//   - it can infer package mode from `preview.category === "Pakiet"`,
//   - it selects the correct frontend config:
//       * voucherLayoutConfig
//       * packageLayoutConfig
//   - it can derive theme from templateVersion if explicit theme is absent,
//   - it keeps full backward compatibility with existing voucher-only callers
//     such as ShopPage.
//
// DATA MODEL (FRONTEND PREVIEW)
// -----------------------------
// The `preview` prop is a lightweight render model.
//
// Supported fields include:
//
//   {
//     // Business identity
//     businessMode?: "voucher" | "package"
//     category?: string
//     type?: string
//
//     // Branding / theme
//     theme?: string
//     salonName?: string
//     logoUrl?: string
//
//     // Amount
//     value?: number | string         // canonical field
//     amount?: number | string        // legacy alias
//     currency?: string               // e.g. "PLN"
//
//     // People
//     recipientName?: string
//     buyerName?: string
//
//     // Message
//     message?: string
//
//     // Validity
//     expiresAtLabel?: string
//
//     // Voucher number (preview-only)
//     voucherNo?: string
//     voucherNumberPlaceholder?: string
//
//     // QR placeholder label
//     qrPlaceholderLabel?: string
//
//     // Layout metadata
//     templateType?: string
//     templateVersion?: string
//   }
//
// NOTE
// ----
// - We support both canonical fields (value, voucherNo) and older aliases
//   (amount, voucherNumberPlaceholder) to stay backward compatible.
// - The actual QR image is NOT rendered here. We only show a placeholder
//   tile to mimic the PDF layout.
// - This component does NOT route backend templates. It only previews the
//   business/render model in HTML.
//

import React from "react";
import { useTranslation } from "react-i18next";
import "./VoucherPreview.css";

import {
  voucherLayoutConfig,
  normalizeTheme as normalizeVoucherTheme,
} from "../config/voucherLayoutConfig";

import {
  packageLayoutConfig,
  normalizeTheme as normalizePackageTheme,
} from "../config/packageLayoutConfig";

// ---------------------------------------------------------------------------
// BUSINESS MODE HELPERS
// ---------------------------------------------------------------------------

/**
 * Canonical preview business modes.
 *
 * We keep them as plain strings because they are:
 *   - easy to inspect in devtools,
 *   - easy to pass through preview models,
 *   - aligned with backend PDF routing concepts.
 */
const PREVIEW_BUSINESS_MODES = Object.freeze({
  VOUCHER: "voucher",
  PACKAGE: "package",
});

/**
 * getBusinessModeFromPreview(preview)
 *
 * Resolve the business mode for the preview model.
 *
 * Preferred order:
 *   1) explicit preview.businessMode if present and valid
 *   2) preview.category === "Pakiet" => package
 *   3) fallback => voucher
 *
 * Why keep this logic here even if upstream already maps it?
 * ----------------------------------------------------------
 * Because this component should stay resilient during migration.
 * Some callers may already provide businessMode explicitly,
 * some may still only provide category,
 * and ShopPage still remains voucher-only.
 */
function getBusinessModeFromPreview(preview) {
  const explicit = String(preview?.businessMode || "")
    .trim()
    .toLowerCase();

  if (explicit === PREVIEW_BUSINESS_MODES.PACKAGE) {
    return PREVIEW_BUSINESS_MODES.PACKAGE;
  }

  if (explicit === PREVIEW_BUSINESS_MODES.VOUCHER) {
    return PREVIEW_BUSINESS_MODES.VOUCHER;
  }

  return preview?.category === "Pakiet"
    ? PREVIEW_BUSINESS_MODES.PACKAGE
    : PREVIEW_BUSINESS_MODES.VOUCHER;
}

/**
 * getLayoutConfigForBusinessMode(mode)
 *
 * Select the correct layout config for the active business family.
 *
 * Defensive fallback:
 *   unknown => voucherLayoutConfig
 */
function getLayoutConfigForBusinessMode(mode) {
  return mode === PREVIEW_BUSINESS_MODES.PACKAGE
    ? packageLayoutConfig || voucherLayoutConfig
    : voucherLayoutConfig;
}

/**
 * normalizeThemeForMode(theme, mode)
 *
 * Normalize a theme using the correct config-side normalizer.
 *
 * Why not only use voucher normalizeTheme?
 * ----------------------------------------
 * Because package config is conceptually allowed to evolve independently.
 * Today it may behave similarly, but the component should already respect
 * the split architecture.
 */
function normalizeThemeForMode(theme, mode) {
  return mode === PREVIEW_BUSINESS_MODES.PACKAGE
    ? normalizePackageTheme(theme)
    : normalizeVoucherTheme(theme);
}

/**
 * deriveThemeFromTemplateVersion(templateVersion, mode, config)
 *
 * Derive a stable preview theme when caller does not provide `theme`.
 *
 * Priority:
 *   1) config.templateVersionToThemeMap[templateVersion]
 *   2) try to normalize templateVersion itself as a theme-like key
 *   3) fallback to config.defaultThemeKey
 *
 * This is particularly important for packages because package templateVersion
 * values can differ from classic voucher motif names.
 */
function deriveThemeFromTemplateVersion(templateVersion, mode, config) {
  const fallback = config?.defaultThemeKey || "default";

  if (!templateVersion) {
    return fallback;
  }

  const v = String(templateVersion).trim().toLowerCase();
  const mapped = config?.templateVersionToThemeMap?.[v];

  if (mapped) {
    return normalizeThemeForMode(mapped, mode);
  }

  return normalizeThemeForMode(v, mode) || fallback;
}

// ---------------------------------------------------------------------------
// PRESENTATION HELPERS
// ---------------------------------------------------------------------------

/**
 * getTemplateTypeLabel(templateType)
 *
 * Human-readable label for current templateType.
 *
 * IMPORTANT:
 * ----------
 * Template type is currently still technically shared between voucher and
 * package families. Therefore the label mapping here can remain business-neutral.
 *
 * If later packages need their own user-facing wording for template types,
 * this helper is the right place to extend.
 */
function getTemplateTypeLabel(templateType, t) {
  if (!templateType) return null;

  return t(`vouchers:templateTypes.${templateType}.label`, {
    defaultValue: templateType,
  });
}

/**
 * getDefaultDocumentLabel(mode, config)
 *
 * Resolve the main document label shown in the preview header.
 *
 * Preferred source:
 *   - active config.voucherLabel
 *
 * Why keep property name `voucherLabel` even for package config?
 * --------------------------------------------------------------
 * Because the config contract was originally created for voucher preview.
 * To keep migration safe, we allow package config to expose the same field
 * name even if its actual value becomes, for example, "Pakiet podarunkowy".
 */
function getDefaultDocumentLabel(mode, config, t) {
  const configFallbackLabel =
    config?.voucherLabel ||
    (mode === PREVIEW_BUSINESS_MODES.PACKAGE ? "Package" : "Voucher");

  // IMPORTANT I18N NOTE
  // -------------------
  // The voucher/package layout configs still contain historical Polish display
  // strings because they originally served the Polish-only shop/PDF preview.
  // The component must not render those config strings directly in English UI.
  // Instead, config values are kept only as defensive defaultValue fallbacks.
  return mode === PREVIEW_BUSINESS_MODES.PACKAGE
    ? t("vouchers:preview.documentLabelPackage", {
        defaultValue: configFallbackLabel,
      })
    : t("vouchers:preview.documentLabelVoucher", {
        defaultValue: configFallbackLabel,
      });
}

/**
 * getDefaultAmountLabel(mode)
 *
 * User-facing section label for the amount box.
 */
function getDefaultAmountLabel(mode, t) {
  return mode === PREVIEW_BUSINESS_MODES.PACKAGE
    ? t("vouchers:overview.valueLabel", { defaultValue: "Value:" })
    : t("vouchers:overview.valueLabel", { defaultValue: "Value:" });
}

/**
 * getDefaultValidityLabel(mode)
 *
 * User-facing section label for the validity block.
 */
function getDefaultValidityLabel(mode, t) {
  return mode === PREVIEW_BUSINESS_MODES.PACKAGE
    ? t("vouchers:overview.expiresLabel", { defaultValue: "Expires:" })
    : t("vouchers:overview.expiresLabel", { defaultValue: "Expires:" });
}

/**
 * getDefaultValidityText(mode, config, t)
 *
 * Resolve the generic validity text shown in the preview body.
 *
 * IMPORTANT:
 * Historical config-level `validityText` values are Polish because the first
 * shop implementation was delivered for Polish customers before the public
 * i18n layer existed. They are now used only as defensive fallbacks, while the
 * normal UI text comes from i18next.
 */
function getDefaultValidityText(mode, config, t) {
  const configFallbackText =
    config?.validityText ||
    (mode === PREVIEW_BUSINESS_MODES.PACKAGE
      ? "Package valid for 12 months from the purchase date."
      : "Voucher valid for 12 months from the purchase date.");

  return mode === PREVIEW_BUSINESS_MODES.PACKAGE
    ? t("vouchers:preview.defaultValidityTextPackage", {
        defaultValue: configFallbackText,
      })
    : t("vouchers:preview.defaultValidityTextVoucher", {
        defaultValue: configFallbackText,
      });
}

/**
 * getRecipientPlaceholder(mode)
 *
 * Placeholder text when recipient name is absent.
 *
 * Even though package create/edit flow should normally enforce recipient_name,
 * the preview remains defensive and user-friendly.
 */
function getRecipientPlaceholder(mode, t) {
  return mode === PREVIEW_BUSINESS_MODES.PACKAGE
    ? t("vouchers:voucherPage.recipientNamePlaceholderPackage", { defaultValue: "Person who will use the package (required)" })
    : t("vouchers:voucherPage.recipientNamePlaceholderVoucher", { defaultValue: "Person who will use the voucher (optional)" });
}

/**
 * getMessagePlaceholder(mode)
 *
 * Placeholder text shown when message/description is empty.
 *
 * We keep it intentionally generic. The preview is illustrative, not
 * a legally exact final PDF representation.
 */
function getMessagePlaceholder(mode, t) {
  return mode === PREVIEW_BUSINESS_MODES.PACKAGE
    ? t("vouchers:voucherPage.descriptionPlaceholderPackage", { defaultValue: "Optional description for this package" })
    : t("vouchers:voucherPage.descriptionPlaceholderVoucher", { defaultValue: "Optional description for this voucher" });
}

/**
 * getPreviewFooterNote()
 *
 * Small neutral disclaimer shown in the preview footer.
 *
 * We intentionally keep it business-neutral because both voucher and package
 * previews are only approximate HTML previews of the final PDF.
 */
function getPreviewFooterNote(t) {
  return t("vouchers:preview.footerNote", {
    defaultValue:
      "Preview only – the final PDF may differ slightly from this version.",
  });
}

/**
 * getDefaultNumberPlaceholder(t)
 *
 * Preview-only number placeholder. In the public shop the real voucher number
 * is generated only after the paid order creates the final voucher record.
 */
function getDefaultNumberPlaceholder(t) {
  return t("vouchers:preview.numberPlaceholder", {
    defaultValue: "Assigned after purchase",
  });
}

/**
 * getDefaultQrPlaceholder(t)
 *
 * Preview-only QR placeholder. The real QR is deliberately not rendered here
 * because the preview is shown before the final voucher exists.
 */
function getDefaultQrPlaceholder(t) {
  return t("vouchers:preview.qrPlaceholder", {
    defaultValue: "QR code will be generated after purchase.",
  });
}

// ---------------------------------------------------------------------------
// COMPONENT
// ---------------------------------------------------------------------------

export const VoucherPreview = ({ preview }) => {
  const { t } = useTranslation(["vouchers"]);

  // Nothing to preview -> nothing to render.
  // Hook is intentionally called before this guard to follow React hook rules.
  if (!preview) return null;

  // -------------------------------------------------------------------------
  // 1) Resolve business mode + correct frontend config
  // -------------------------------------------------------------------------
  const businessMode = getBusinessModeFromPreview(preview);
  const activeLayoutConfig = getLayoutConfigForBusinessMode(businessMode);

  // -------------------------------------------------------------------------
  // 2) Destructure preview model
  // -------------------------------------------------------------------------
  //
  // We keep the preview object around conceptually, but destructure the main
  // fields for clarity and stable fallbacks.
  //
  const {
    theme,
    salonName: previewSalonName,
    logoUrl,

    amount, // legacy alias
    value,  // canonical

    currency = "PLN",

    recipientName,
    buyerName,
    message,

    expiresAtLabel,

    voucherNo, // canonical
    voucherNumberPlaceholder, // legacy alias

    qrPlaceholderLabel,

    templateType,
    templateVersion,
  } = preview;

  // -------------------------------------------------------------------------
  // 3) Theme resolution
  // -------------------------------------------------------------------------
  //
  // Preferred order:
  //   1) explicit preview.theme
  //   2) derive from preview.templateVersion
  //   3) fallback to active config default
  //
  const themeKey = theme
    ? normalizeThemeForMode(theme, businessMode)
    : deriveThemeFromTemplateVersion(
        templateVersion,
        businessMode,
        activeLayoutConfig
      );

  const rootThemeClass = `vp-root--${themeKey}`;

  // Extra class for future family-specific CSS differentiation.
  // It is harmless if CSS does not define it yet.
  const rootBusinessClass = `vp-root--mode-${businessMode}`;

  // -------------------------------------------------------------------------
  // 4) Salon / document labels
  // -------------------------------------------------------------------------
  //
  // Salon name:
  //   - allow override via preview.salonName
  //   - otherwise fall back to active layout config
  //
  const salonName =
    (previewSalonName && String(previewSalonName).trim()) ||
    activeLayoutConfig?.salonName ||
    "HB Health & Beauty";

  const documentLabel = getDefaultDocumentLabel(
    businessMode,
    activeLayoutConfig,
    t
  );

  const amountLabel = getDefaultAmountLabel(businessMode, t);
  const validityLabel = getDefaultValidityLabel(businessMode, t);

  // -------------------------------------------------------------------------
  // 5) Validity text
  // -------------------------------------------------------------------------
  //
  // - prefer explicit per-document label
  // - otherwise use config-level generic validity text
  //
  const defaultValidityText = getDefaultValidityText(
    businessMode,
    activeLayoutConfig,
    t
  );

  const displayExpires = expiresAtLabel || defaultValidityText;

  // -------------------------------------------------------------------------
  // 6) Voucher / package visible number
  // -------------------------------------------------------------------------
  //
  // We keep the older prop name `voucherNumberPlaceholder` for compatibility,
  // even though admin preview usually shows a real number.
  //
  const displayVoucherNumber =
    (voucherNo && String(voucherNo).trim()) ||
    (voucherNumberPlaceholder && String(voucherNumberPlaceholder).trim()) ||
    getDefaultNumberPlaceholder(t);

  // -------------------------------------------------------------------------
  // 7) QR placeholder label
  // -------------------------------------------------------------------------
  //
  // This remains purely informational. No real QR payload is rendered here.
  //
  const displayQrLabel =
    (qrPlaceholderLabel && String(qrPlaceholderLabel).trim()) ||
    getDefaultQrPlaceholder(t);

  // -------------------------------------------------------------------------
  // 8) Amount
  // -------------------------------------------------------------------------
  //
  // Preferred order:
  //   1) canonical `value`
  //   2) legacy `amount`
  //
  const rawAmount = value != null ? value : amount;
  const numericAmount = Number(rawAmount);

  const formattedAmount = Number.isFinite(numericAmount)
    ? numericAmount.toFixed(2).replace(".", ",")
    : "—";

  // -------------------------------------------------------------------------
  // 9) Recipient / buyer / message
  // -------------------------------------------------------------------------
  const displayRecipient =
    recipientName && String(recipientName).trim()
      ? String(recipientName).trim()
      : getRecipientPlaceholder(businessMode, t);

  const displayMessage =
    message && String(message).trim()
      ? String(message).trim()
      : getMessagePlaceholder(businessMode, t);

  const displayBuyerName =
    buyerName && String(buyerName).trim()
      ? String(buyerName).trim()
      : null;

  // -------------------------------------------------------------------------
  // 10) Template type human-readable label
  // -------------------------------------------------------------------------
  const displayTemplateType = getTemplateTypeLabel(templateType, t);

  // -------------------------------------------------------------------------
  // 11) Render
  // -------------------------------------------------------------------------
  return (
    <div
      className={`vp-root ${rootThemeClass} ${rootBusinessClass}`}
      data-business-mode={businessMode}
      data-template-type={templateType || ""}
      data-template-version={templateVersion || ""}
    >
      <div className="vp-page">
        {/* HEADER: logo + salon name + document label + visible number */}
        <header className="vp-header">
          <div className="vp-header-left">
            {logoUrl ? (
              <img src={logoUrl} alt={salonName} className="vp-logo-img" />
            ) : (
              <div className="vp-logo-placeholder">{t("vouchers:preview.logoPlaceholder")}</div>
            )}

            <div className="vp-header-text">
              <div className="vp-salon-name">{salonName}</div>
              <div className="vp-subtitle">{documentLabel}</div>
            </div>
          </div>

          <div className="vp-header-right">
            <div className="vp-label">{t("vouchers:preview.numberLabel")}</div>
            <div className="vp-voucher-number">{displayVoucherNumber}</div>
          </div>
        </header>

        {/* AMOUNT BOX */}
        <section className="vp-amount-box">
          <div className="vp-amount-label">{amountLabel}</div>

          <div className="vp-amount-value">
            {formattedAmount}{" "}
            <span className="vp-amount-currency">{currency}</span>
          </div>

          {displayBuyerName && (
            <div className="vp-amount-note">
              {t("vouchers:voucherPage.buyerNameLabel")} <span>{displayBuyerName}</span>
            </div>
          )}
        </section>

        {/* RECIPIENT SECTION */}
        <section className="vp-section">
          <div className="vp-label">{t("vouchers:preview.forLabel")}</div>
          <div className="vp-value vp-value--recipient">
            {displayRecipient}
          </div>
        </section>

        {/* MESSAGE SECTION */}
        <section className="vp-section">
          <div className="vp-label">{t("vouchers:preview.messageLabel")}</div>
          <div className="vp-value vp-value--message">{displayMessage}</div>
        </section>

        {/* FOOTER: validity + QR placeholder + template type */}
        <footer className="vp-footer">
          <div className="vp-footer-left">
            <div className="vp-label">{validityLabel}</div>
            <div className="vp-value">{displayExpires}</div>

            {displayTemplateType && (
              <div className="vp-footer-layout">
                {t("vouchers:voucherPage.templateTypeLabel")} <span>{displayTemplateType}</span>
              </div>
            )}

            <div className="vp-footer-note">{getPreviewFooterNote(t)}</div>
          </div>

          <div className="vp-footer-right">
            <div className="vp-qr-box">
              <div className="vp-qr-label">{t("vouchers:preview.qrCodeLabel")}</div>
              <div className="vp-qr-placeholder">
                <span>{displayQrLabel}</span>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

